import { z } from 'zod'
import { api } from '@/shared/api/client'
import { errorTag } from '@/shared/api/errors'

export type EntryMethod = 'CODE' | 'QR_SCAN' | 'QR_IMAGE' | 'QR_LINK'

export type VoucherCredential = { code: string } | { qrToken: string }

const CODE_SHAPE = /^[A-Z0-9-]{6,44}$/
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{40,64}$/
const CARD_LINK = /^https?:\/\/[^\s#]+\/v\/?#(.+)$/i

export function normalizeVoucherCode(raw: string): string | null {
  const code = raw.replace(/\s+/g, '').toUpperCase()
  return CODE_SHAPE.test(code) ? code : null
}

export function isCardLink(raw: string): boolean {
  return CARD_LINK.test(raw.trim())
}

export function parseVoucherText(raw: string): VoucherCredential | null {
  const text = raw.trim()
  const link = CARD_LINK.exec(text)
  if (link) return fromFragment(link[1] ?? '')
  if (/^https?:\/\//i.test(text)) return null
  const code = normalizeVoucherCode(text)
  return code ? { code } : null
}

function fromFragment(fragment: string): VoucherCredential | null {
  let decoded: string
  try {
    decoded = decodeURIComponent(fragment).trim()
  } catch {
    return null
  }
  if (decoded.startsWith('t.')) {
    const qrToken = decoded.slice(2)
    return TOKEN_SHAPE.test(qrToken) ? { qrToken } : null
  }
  const code = normalizeVoucherCode(decoded)
  return code ? { code } : null
}

const REJECTION_KEYS: Record<string, string> = {
  VOUCHER_NOT_FOUND: 'voucher.errors.notFound',
  VOUCHER_ACTIVATED: 'voucher.errors.activated',
  VOUCHER_EXPIRED: 'voucher.errors.expired',
  VOUCHER_VOIDED: 'voucher.errors.voided',
  VOUCHER_UNUSABLE: 'voucher.errors.unusable',
}

export function voucherRejectionKey(error: unknown): string | null {
  const tag = errorTag(error)
  return tag ? (REJECTION_KEYS[tag] ?? null) : null
}

const redeemedSchema = z.object({
  grantId: z.string(),
  planName: z.string().nullish(),
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  expiresAt: z.string().nullish(),
})

export interface VoucherRedeemed {
  grantId: string
  planName: string | null
  quotaBytes: number | null
  quotaSeconds: number | null
  expiresAt: string | null
}

export interface RedeemRequest {
  credential: VoucherCredential
  entryMethod: EntryMethod
}

export async function redeemVoucher({ credential, entryMethod }: RedeemRequest): Promise<VoucherRedeemed> {
  const response = await api.post<unknown>('/api/selfcare/vouchers/redeem', { ...credential, entryMethod })
  const parsed = redeemedSchema.parse(response.data)
  return {
    grantId: parsed.grantId,
    planName: parsed.planName?.trim() || null,
    quotaBytes: parsed.quotaBytes ?? null,
    quotaSeconds: parsed.quotaSeconds ?? null,
    expiresAt: parsed.expiresAt ?? null,
  }
}
